import _ from "lodash";
import React, { PropTypes } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { bindActionCreators } from "redux";
import simpleFetch from "../../../../../../../utils/simpleFetch";
import * as types from "../../../../../../utils/data/dataActionTypes";
import * as dataActions from "../../../../../../utils/data/dataActions";
import { CLIENT_CONTACTS } from "../../../../../../utils/data/models";
import { RectangleSpinner } from "../../../../../common/spinners";
import { KEYS_TO_CLEAR, KEYS_TO_POST, TOTAL_CONTACTS } from "../helpers/contactDataHelper";
import StrategicFieldsForm from "./StrategicContactForm";
import { isSimpleFetchToast } from "../../../../../common/RouteValidators/ClientValidationHelper";

class StrategicContactsPage extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            submittingForm: false,
            hasResponse: false,
            clientContacts: [],
            initialValues: undefined,
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onSubmit = _.debounce(this.onSubmit.bind(this), 350); // Debounce func to avoid button spamming
        this.archiveClientContact = this.archiveClientContact.bind(this);
        this.getClientContacts = this.getClientContacts.bind(this);
        this.createInitialValues = this.createInitialValues.bind(this);
        this.clearValues = this.clearValues.bind(this);
    }
    componentDidMount() {
        this.getClientContacts();
    }
    getClientContacts() {
        try {
            simpleFetch("GET", CLIENT_CONTACTS.path.replace("{}", this.props.activeClient.clientId), {}).then(
                (response) => {
                    this.setState({ hasResponse: true, clientContacts: response });
                    this.createInitialValues();
                }
            );
        } catch (e) {
            /** If a promise error for unauthorized is thrown (403) by simpleFetch we toast error. If not, we throw the error. */
            if (e.status === 403) {
                toast(<FormattedMessage id="error.403.title" />, {
                    type: toast.TYPE.ERROR,
                    autoClose: false,
                });
            } else {
                toast(<FormattedMessage id="error.unknown" />, {
                    type: toast.TYPE.ERROR,
                    autoClose: false,
                });
            }
            throw e;
        }
    }
    createInitialValues() {
        const { clientContacts } = this.state;
        const initialValues = Array.from({ length: TOTAL_CONTACTS.length }, (_, i) => {
            const contactIndex = i + 1;
            // Checks if the client contact exists in the database.
            // If found, adds the client's contact information to initialValues.
            // If not found, initializes basic values using contactIndex.
            const matchedClient = clientContacts
                ? clientContacts.find((client) => client.contactIndex === contactIndex)
                : null;
            const allowLinkedinLink = matchedClient ? !!matchedClient.linkedin : false;
            return {
                contactIndex,
                allowLinkedinLink,
                ...(matchedClient ? matchedClient : {}), // Merge matched client properties if found
            };
        });

        this.setState({
            initialValues,
        });
    }

    onSubmit(values) {
        /**
         * Handles the form submission for client contacts.
         * This function checks the submitted values against existing contacts in the database.
         * If the number of submitted values exceeds the total number of contacts,
         * it processes each contact, verifies their details,
         * and ensures that there are no duplicate primary strategic contacts.
         * Valid contacts are then prepared for submission to the server.
         * Updates the contact index values by calling the function getClientContacts
         * The function also manages error handling and success notifications.
         */
        this.setState({submittingForm: true});
        if (Object.keys(values).length > TOTAL_CONTACTS.length) {
            // Equal values means the object contains only contact indexes
            const { activeClient, dataActions } = this.props;
            let valuesToPost = [];
            let totalMainStrategic = {};

            Array.from({ length: TOTAL_CONTACTS.length }, (_, i) => i + 1).forEach((contactIndex) => {
                let clientContact = {};

                Object.entries(values).forEach(([key, value]) => {
                    let keyToCompare = contactIndex.toString();
                    const roleKey = `role-${keyToCompare}`;
                    if (values.hasOwnProperty(roleKey) && values[roleKey] && values[roleKey].trim() != "") {
                        if (key == `isMainStrategic-${keyToCompare}`) {
                            // Checks if client has more than one primary strategic contact
                            if (value == true && !totalMainStrategic.hasOwnProperty(contactIndex)) {
                                totalMainStrategic[contactIndex] = value;
                            }
                        }
                        if (key.includes(keyToCompare)) {
                            // The form values are structured as "fieldName-{contactIndex}".
                            // We remove the suffix after the '-' to ensure compatibility with the database structure.
                            clientContact[key.split("-")[0]] = value; // Split the key and assign the value
                        }
                    }
                });
                if (Object.keys(clientContact).length > 1) {
                    //One length indicates that the object only contains contactIndex
                    clientContact["clientId"] = activeClient.clientId;
                    clientContact["note"] = clientContact.note ? clientContact.note.trim() : null;
                    clientContact["linkedin"] = clientContact.linkedin ? clientContact.linkedin.trim(): null;
                    clientContact["firstName"] = clientContact.firstName ? clientContact.firstName.trim(): null;
                    clientContact["lastName"] = clientContact.lastName ? clientContact.lastName.trim(): null;

                    //Not null field for database
                    if (!clientContact.clientContactId) {
                        // Ensure the client with current contactIndex does not already exist in the database.
                        // This compares the value with the most recently fetched contacts ( In order to avoid any conflicts);
                        // if the clientContactId is found, it adds the contact to the update dictionary.
                        const existingContact = this.state.clientContacts.filter(
                            (contact) =>
                                contact.contactIndex === clientContact.contactIndex && contact.clientContactId != null
                        );
                        if (existingContact && existingContact.length == 1)
                            clientContact.clientContactId = existingContact[0].clientContactId;
                    }
                    // Add missing keys with empty strings
                    if (clientContact.clientContactId) {
                        // The form does not keep the values if we delete them
                        // In order to delete already existing values we add null value if values are empty
                        KEYS_TO_POST.forEach((key) => {
                            if (!clientContact.hasOwnProperty(key)) {
                                clientContact[key] = null;
                            }
                        });
                    }
                    valuesToPost.push(clientContact); // Push only valid contacts
                }
            });
            if (Object.keys(totalMainStrategic).length > 1) {
                toast(<FormattedMessage id="clientContacts.form.strategicContact.mainStrategicMessage" />, {
                    type: toast.TYPE.ERROR,
                    autoClose: false,
                });
                return;
            }

            // Define model for the post request
            const model = {
                path: CLIENT_CONTACTS.path.replace("{}", activeClient.clientId),
            };
            try {
                dataActions.createDataItem(model, valuesToPost).then((data) => {
                    const simpleFetchToast = isSimpleFetchToast(data);
                    
                    if(!simpleFetchToast) {
                        if (data.type == types.CREATE_DATA_ITEM_FAILURE) {
                            toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                                type: toast.TYPE.ERROR,
                                autoClose: false,
                            });
                        } else {
                            this.getClientContacts();
                            toast(<FormattedMessage id="clientContacts.successStrategicToast" />, {
                                type: toast.TYPE.SUCCESS,
                                autoClose: 5000,
                            });
                        }
                    }
                    this.setState({ submittingForm: false });
                });
            } catch (e) {
                toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                    type: toast.TYPE.ERROR,
                    autoClose: false,
                });
            }
        }
    }
    clearValues(contactIndex) {
        const { changeFieldValue } = this.props;
        KEYS_TO_CLEAR.map((val) => {
            changeFieldValue("strategicContactForm", `${val}-${contactIndex}`, null);
        });
        changeFieldValue("strategicContactForm", `isMainStrategic-${contactIndex}`, false);
    }

    archiveClientContact(clientContactId,contactIndex) {
        const { activeClient, dataActions } = this.props;
        const model = { ...CLIENT_CONTACTS, path: CLIENT_CONTACTS.path.replace("{}", activeClient.clientId) };
        if (clientContactId) {
            try {
                const valuesToPost = {
                    clientContactId: clientContactId,
                    isArchived: true,
                };
                dataActions.updateDataItem(model, valuesToPost).then((data) => {
                    const simpleFetchToast = isSimpleFetchToast(data);
                    if(!simpleFetchToast) {
                        if (data.type == types.UPDATE_DATA_ITEM_FAILURE) {
                            toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                                type: toast.TYPE.ERROR,
                                autoClose: false,
                            });
                            this.setState({ submittingForm: false });
                        } else {
                            this.getClientContacts();
                            toast(<FormattedMessage id="clientContacts.archiveToast" values={{archivedName : contactIndex}} />, {
                                type: toast.TYPE.SUCCESS,
                                autoClose: 5000,
                            });
                        }
                    }
                });
            } catch (e) {
                toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                    type: toast.TYPE.ERROR,
                    autoClose: false,
                });
            }
        }
        else{
            toast(<FormattedMessage id="clientContacts.archiveToast" values={{archivedName : contactIndex}} />, {
                type: toast.TYPE.SUCCESS,
                autoClose: 5000,
            });
        }
        this.setState({ submittingForm: false });
    }

    render() {
        const { activeClient, disabled, dataActions, changeFieldValue } = this.props;
        const { submittingForm, initialValues } = this.state;
        return activeClient && this.state.hasResponse && initialValues ? (
            <StrategicFieldsForm
                submittingForm={submittingForm}
                client={activeClient}
                disabled={disabled}
                dataActions={dataActions}
                totalContacts={TOTAL_CONTACTS}
                clientContacts={initialValues}
                keysToPost={KEYS_TO_POST}
                archiveClientContact={this.archiveClientContact}
                changeFieldValue={changeFieldValue}
                clearValues={this.clearValues}
                onSubmit={this.onSubmit}
            />
        ) : (
            <RectangleSpinner />
        );
    }
}

StrategicContactsPage.propTypes = {
    changeFieldValue: PropTypes.func.isRequired,
    activeClient: PropTypes.object,
    disabled: PropTypes.bool,
    dataActions: PropTypes.object,
    userList: PropTypes.array,
};

function mapDispatchToProps(dispatch) {
    return {
        dataActions: bindActionCreators(dataActions, dispatch),
    };
}

export default connect(null, mapDispatchToProps)(StrategicContactsPage);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/contacts/strategic-contacts/StrategicContactsPage.js